import { useState } from 'react';
import { NavLink, Route, Routes } from 'react-router-dom';
import { Coins, FileText, Inbox, LayoutDashboard, LogOut, Menu, Server, Settings, Users, X } from 'lucide-react';
import { clearOperatorToken, operatorApi, useFetch } from './services/operatorApi';
import OperatorDashboard from './pages/operator/DashboardPage';
import ClientsPage from './pages/operator/ClientsPage';
import ClientDetailPage from './pages/operator/ClientDetailPage';
import OperatorDocumentsPage from './pages/operator/DocumentsPage';
import DocumentDetailPage from './pages/operator/DocumentDetailPage';
import CreditsPage from './pages/operator/CreditsPage';
import SystemPage from './pages/operator/SystemPage';
import OperatorSettingsPage from './pages/operator/SettingsPage';
import RequestsPage from './pages/operator/RequestsPage';

const navigation = [
  { name: 'Tableau de bord', to: '/', icon: LayoutDashboard, end: true },
  { name: 'Demandes', to: '/requests', icon: Inbox },
  { name: 'Clients', to: '/clients', icon: Users },
  { name: 'Documents', to: '/documents', icon: FileText },
  { name: 'Ventes et crédits', to: '/credits', icon: Coins },
  { name: 'Système', to: '/system', icon: Server },
  { name: 'Réglages', to: '/settings', icon: Settings },
];

export default function OperatorApp({ onLogout }: { onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const pending = useFetch(async () => (await operatorApi.get<{ pending: number }>('/access-requests')).data.pending).data ?? 0;

  const logout = () => {
    clearOperatorToken();
    onLogout();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-slate-900 text-slate-300 transition-transform lg:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex h-16 items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <img src="/favicon.svg" alt="" className="h-9 w-9" />
            <div>
              <p className="text-base font-semibold leading-tight text-white">Juriseal</p>
              <p className="text-xs text-slate-400">Administration</p>
            </div>
          </div>
          <button className="lg:hidden text-slate-400" onClick={() => setOpen(false)} aria-label="Fermer le menu">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-4">
          {navigation.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? 'bg-indigo-600 text-white' : 'hover:bg-slate-800 hover:text-white'}`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.name}
              {item.to === '/requests' && pending > 0 && <span className="ml-auto rounded-full bg-amber-400 px-2 py-0.5 text-xs font-semibold text-slate-900">{pending}</span>}
            </NavLink>
          ))}
        </nav>
        <button onClick={logout} className="m-3 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-slate-800 hover:text-white">
          <LogOut className="w-5 h-5" />
          Déconnexion
        </button>
      </aside>

      <header className="sticky top-0 z-30 flex h-14 items-center border-b border-slate-200 bg-white px-4 lg:hidden">
        <button onClick={() => setOpen(true)} aria-label="Ouvrir le menu">
          <Menu className="w-6 h-6 text-slate-700" />
        </button>
        <img src="/favicon.svg" alt="" className="ml-3 h-7 w-7" /><span className="ml-2 font-semibold text-slate-900">Juriseal · Administration</span>
      </header>

      <main className="lg:pl-64">
        <div className="mx-auto max-w-7xl p-4 sm:p-8">
          <Routes>
            <Route path="/" element={<OperatorDashboard />} />
            <Route path="/requests" element={<RequestsPage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/clients/:id" element={<ClientDetailPage />} />
            <Route path="/documents" element={<OperatorDocumentsPage />} />
            <Route path="/documents/:id" element={<DocumentDetailPage />} />
            <Route path="/credits" element={<CreditsPage />} />
            <Route path="/system" element={<SystemPage />} />
            <Route path="/settings" element={<OperatorSettingsPage />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
